// Source drawer (RGS-5). Opens on a leaf-page row click. The clicked row's list
// data renders the header instantly; the RGS-3 detail endpoint (lazy) fills the
// signals-supported cards, the 6-month history, and the models.
//
// Deliberately NO "what AI said" quote — per the Jun 12 DeepDive call we don't
// render the verbatim model text (can't honestly attribute a span to a source).
// Signal cards bind to the real API name + category (project_rgs_signal_vocab).
// The Workbench CTA is clickable but inert — the real Workbench is Capability 3.

import React, { useEffect, useRef, useState } from "react";
import type { SourceRow } from "@/types/vb";
import { useReportSource } from "@/hooks/vb/useReportSource";
import { ORBIT_META } from "@/components/vb/OrbitBar";
import { SparkLine, ModelPills } from "./SourceCells";
import { relativeTime, SIGNAL_TONE } from "./sourcesUtil";

interface SourceDrawerProps {
  reportId: number | undefined;
  row: SourceRow | null;
  onClose: () => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  technical: "Foundation",
  authority: "Authority",
  brand: "Brand",
};

export function SourceDrawer({ reportId, row, onClose }: SourceDrawerProps) {
  const open = row != null;
  // Retain the last source through the slide-out so the panel isn't blank.
  const [shown, setShown] = useState<SourceRow | null>(row);
  useEffect(() => {
    if (row) setShown(row);
  }, [row]);

  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const { data: detail, isLoading } = useReportSource(reportId, open ? shown?.id ?? null : null);

  const title = shown ? shown.domain + (shown.path !== "/" ? shown.path : "") : "";
  const orbit = shown?.orbit ?? detail?.orbit ?? null;
  const citations = shown?.citations ?? detail?.citations ?? 0;
  const seen = shown?.last_seen ? relativeTime(shown.last_seen) : "";

  const historySeries = detail?.history.map((h) => h.citations) ?? shown?.series ?? [];
  const unhealthy = detail?.unhealthy_signals_count ?? 0;
  const totalSignals = detail?.signals.length ?? 0;
  // "this signal" only reads right when the source supports a single signal.
  // With more than one shown (e.g. one healthy + one not), name the count so
  // it's unambiguous how many the CTA targets — even when just one is unhealthy.
  const ctaText =
    totalSignals <= 1
      ? "Improve this signal in Workbench →"
      : `Improve ${unhealthy} signal${unhealthy === 1 ? "" : "s"} in Workbench →`;

  return (
    <>
      <div
        className={`src-drawer-overlay${open ? " open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`src-drawer${open ? " open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || "Source detail"}
        aria-hidden={!open}
      >
        <div className="src-dr-hd">
          <div className="src-dr-topline">
            {orbit && (
              <span className="src-pill-orbit">
                <span className={`src-orbit-dot src-orbit-dot--${orbit}`} aria-hidden="true" />
                {ORBIT_META[orbit].tech}
              </span>
            )}
            <span className="src-dr-cadence">
              Cited {citations}× this month{seen ? ` · ${seen}` : ""}
            </span>
          </div>
          <h2 className="src-dr-title">{title}</h2>
          <button
            type="button"
            ref={closeRef}
            className="src-dr-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="src-dr-bd">
          <div className="src-dr-sec">
            <h4>Signals this source supports</h4>
            {isLoading && !detail ? (
              <div className="src-dr-skel" />
            ) : detail && detail.signals.length > 0 ? (
              <div className="src-dr-sigs">
                {detail.signals.map((sig) => {
                  const tone = SIGNAL_TONE[sig.status] ?? SIGNAL_TONE.healthy;
                  return (
                    <div className={`src-dr-sig ${tone.cls}`} key={sig.name}>
                      <span className="top">
                        <span className="nm">{sig.name}</span>
                        <span className={`src-badge-status ${sig.status}`}>{tone.badge}</span>
                      </span>
                      <span className="cat">{CATEGORY_LABEL[sig.category] ?? sig.category}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="src-cell-seen">No specific signals. Cited for general context, not about you specifically.</span>
            )}
          </div>

          <div className="src-dr-sec">
            <h4>Citation history · last 6 months</h4>
            <div className="src-dr-hist">
              <SparkLine series={historySeries} w={140} h={32} />
              <span className="t">
                <strong className="tnum">{citations}</strong> citations this month
                {seen ? ` · ${seen}` : ""}
              </span>
            </div>
          </div>

          <div className="src-dr-sec">
            <h4>AI models citing this source</h4>
            <ModelPills models={detail?.models ?? shown?.models ?? []} max={9} />
          </div>
        </div>

        {unhealthy > 0 && (
          <div className="src-dr-ft">
            {/* Clickable but inert — the real Workbench is Capability 3. */}
            <button type="button" className="vb-btn vb-btn--sm vb-btn--primary" onClick={() => {}}>
              {ctaText}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

export default SourceDrawer;
